import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useState } from "react";
import i18n, { LANGUAGES, DEFAULT_LANGUAGE } from "../localization/i18n";

const LanguageContext = createContext(null);

const STORAGE_KEY = "bigmarket_language";
// Set only when the visitor picks a language themselves, so page-level defaults
// (e.g. Arabic on the marketing pages) never override a real choice.
const EXPLICIT_KEY = "bigmarket_language_explicit";

// Pages that default to a specific language until the visitor picks one.
const PAGE_DEFAULT_LANGUAGES = {
  "/join": "ar",
  "/about-us": "ar",
};

function readStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function hasExplicitChoice() {
  return readStorage(EXPLICIT_KEY) === "1";
}

function readStoredLanguage() {
  const stored = readStorage(STORAGE_KEY);
  return stored && LANGUAGES[stored] ? stored : DEFAULT_LANGUAGE;
}

function readInitialLanguage() {
  if (!hasExplicitChoice()) {
    const path = window.location.pathname.replace(/\/+$/, "");
    const pageDefault = PAGE_DEFAULT_LANGUAGES[path];
    if (pageDefault) return pageDefault;
  }
  return readStoredLanguage();
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(readInitialLanguage);

  useEffect(() => {
    const meta = LANGUAGES[language];
    i18n.changeLanguage(language);
    document.documentElement.lang = language;
    document.documentElement.dir = meta.isRTL ? "rtl" : "ltr";
  }, [language]);

  const setLanguage = useCallback((code) => {
    if (!LANGUAGES[code]) return;
    setLanguageState(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
      localStorage.setItem(EXPLICIT_KEY, "1");
    } catch {
      // ignore storage errors (private browsing, etc.)
    }
  }, []);

  // Applies a non-persisted language while a page is mounted.
  const applyPageDefault = useCallback((code) => {
    if (!LANGUAGES[code] || hasExplicitChoice()) return;
    i18n.changeLanguage(code);
    setLanguageState(code);
  }, []);

  const value = {
    language,
    setLanguage,
    applyPageDefault,
    isRTL: LANGUAGES[language].isRTL,
    languages: LANGUAGES,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

// Shows the page in `code` unless the visitor has already chosen a language.
// On leaving the page, restores the site language if they still haven't chosen.
export function usePageDefaultLanguage(code) {
  const { applyPageDefault } = useLanguage();

  useLayoutEffect(() => {
    applyPageDefault(code);
    return () => {
      if (!hasExplicitChoice()) applyPageDefault(readStoredLanguage());
    };
  }, [code, applyPageDefault]);
}
